import { Mutex } from '@cf-shared/mutex';

/**
 * Serializes the first-time build of each PouchDB map/reduce view, per database instance
 * (#3466).
 *
 * All map/reduce views share one dependent-DB registry doc, `_local/mrviews`, in the source
 * database (pouchdb-abstract-mapreduce). Every first-time view build upserts it, and that
 * upsert is non-atomic (get -> if-404 put). Two concurrent first builds therefore both read
 * the doc as missing and both INSERT it.
 *
 * ORIGINALLY that was fatal on iOS: the capacitor-sqlite adapter surfaced the losing INSERT
 * as a raw `UNIQUE constraint failed: local_store.id` (SQLite code 19) rather than a
 * normalized 409, and pouchdb's own upsert retry only retries a 409 — so the error reached
 * the caller. #4717 fixed that at the source: the adapter now writes a local doc with a
 * single conditional statement (`INSERT OR IGNORE`, then read the affected-row count), so
 * the loser gets a real 409 and the retry recovers, exactly as the IndexedDB adapter has
 * always behaved.
 *
 * This gate is KEPT anyway, for the reason it also always had: it single-flights the first
 * build of each view, so several cold builds do not all hammer the one native bridge at
 * once. It is no longer the only thing standing between the race and a thrown error, but
 * removing it would put the boot-time build storm back. Once a view has built successfully
 * it is remembered and runs immediately (concurrently) like before — the serialization only
 * applies to cold first builds, so there is no steady-state query overhead.
 *
 * `built` is tracked per PouchDB instance: `_local/mrviews` lives in the source db, and the
 * caller's primary db can switch within one wrapper (shared-db remote->local, an
 * impersonation/coach temp db, or a recreated localDb). A fresh instance is a fresh object,
 * so it gets a fresh (absent) `built` set automatically — a view built against one instance
 * is never assumed built against another.
 */
export class ViewBuildSerializer {
  // single-flight lock so the shared `_local/mrviews` registry is never upserted concurrently
  private buildLock = new Mutex();
  // views already built, keyed by PouchDB instance (see class comment for why per-instance)
  private builtByDb = new WeakMap<object, Set<string>>();

  // returns the built-views set for a db instance, creating it on first use (synchronous, so
  // concurrent callers for the same instance share one set)
  private builtFor(db: object): Set<string> {
    let built = this.builtByDb.get(db);
    if (!built) {
      built = new Set<string>();
      this.builtByDb.set(db, built);
    }
    return built;
  }

  /**
   * Runs `runQuery` for `viewName` against `db`. An already-built view runs immediately and
   * concurrently. The first build of a view (per db instance) is serialized behind any other
   * in-flight first build.
   *
   * @param wasBuilt decides whether a result counts as a successful build. A result that did
   *   not actually build the view (e.g. a query intercepted during db recovery/shutdown)
   *   should return false so the view stays serialized and the next attempt retries under
   *   the lock rather than racing.
   */
  async query<T>(db: object, viewName: string, runQuery: () => Promise<T>, wasBuilt: (result: T) => boolean): Promise<T> {
    const built = this.builtFor(db);
    // if the view is already built on this instance, query it directly (concurrent)
    if (built.has(viewName)) {
      return runQuery();
    }
    return this.buildLock.exclusiveCall(async () => {
      // if a peer built this view while we waited for the lock, query it directly
      if (built.has(viewName)) {
        return runQuery();
      }
      const result = await runQuery();
      if (wasBuilt(result)) {
        built.add(viewName);
      }
      return result;
    });
  }
}
